import { useMutation, useQuery } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { Pagination } from '@/types/shared';

/**
 * Reconciliation — DataCentral v4 `/reconciliation/cycles` endpoints.
 * Auth: UMA RPT via `dataCentralApi` (same as all DC v4 data).
 *
 * Envelope shapes:
 *   List: { message, pagination, results: T[] }
 *   Object: { message, result: T }
 */

/* ── Types ──────────────────────────────────────────────────────────────── */

export type CycleStatus = 'reconciling' | 'done';
export type ServiceItemStatus = 'issued' | 'pending';
export type InvoiceItemStatus = string; // backend-defined, e.g. "issued", "paid", "overdue"

/** One row from `GET /reconciliation/cycles`. */
export interface CycleListItem {
  period_startdate: string;
  period_enddate: string;
  /** "YYYY-MM-DD_YYYY-MM-DD" — used as path param. */
  cohort_key: string;
  total_services: number;
  issued_count: number;
  pending_count: number;
  /** Total invoice amount in VND (raw number). */
  total_amount: number;
  mail_sent_count: number;
  status: CycleStatus;
}

/** One service row inside a cycle detail. */
export interface ServiceItem {
  service_id: string;
  service_name: string;
  product_group_name: string;
  customer_id: number;
  customer_name: string;
  company_name: string;
  status: ServiceItemStatus;
  invoice_id?: number | null;
  invoice_code?: string | null;
}

/** One invoice line item inside an invoice. */
export interface InvoiceLineItem {
  [key: string]: unknown;
}

/** One invoice row inside a cycle detail. */
export interface InvoiceItem {
  invoice_id: number;
  invoice_code: string;
  customer_id: number;
  customer_name: string;
  company_name: string;
  invoice_total: number;
  invoice_amount: number;
  invoice_vat: number;
  invoice_description?: string | null;
  invoice_apply_startdate?: string | null;
  invoice_apply_enddate?: string | null;
  invoice_status: InvoiceItemStatus;
  invoice_duedate?: string | null;
  invoice_type: string;
  invoice_mail_count: number;
  service_count: number;
  invoice_items: InvoiceLineItem[];
  entity_service_name?: string | null;
}

/** Full detail from `GET /reconciliation/cycles/{cohortKey}`. */
export interface CycleDetail {
  period_startdate: string;
  period_enddate: string;
  cohort_key: string;
  issued_count: number;
  pending_count: number;
  total_amount: number;
  mail_sent_count: number;
  status: CycleStatus;
  services: ServiceItem[];
  invoices: InvoiceItem[];
}

/** Result from email preview or send-status. */
export interface BatchStatus {
  batch_id?: string;
  status?: string;
  total?: number;
  sent?: number;
  failed?: number;
  errors?: string[];
  [key: string]: unknown;
}

/** Email preview result. */
export interface EmailPreviewResult {
  html: string;
  recipients: string[];
}

/* ── Send-email body ────────────────────────────────────────────────────── */

export type SendEmailScope = 'all' | 'pending' | 'selected' | 'invoice_ids';

export interface SendEmailBody {
  scope: SendEmailScope;
  invoice_ids?: number[];
  schedule_at?: string | null;
  test_recipient?: string | null;
  /** Must be true for live sends (non-test). */
  confirm?: boolean;
  force_resend?: boolean;
  /** Default true: skip invoices with no sendable customer_mailer. false = fall back to customer + contact emails. */
  strict_mailer?: boolean;
  /** Backend default true: invoices of 0 đồng are left out. false = send them anyway. */
  skip_zero?: boolean;
}

/* ── Params ─────────────────────────────────────────────────────────────── */

export interface ReconciliationCyclesParams {
  page?: number;
  page_size?: number;
  period_from?: string;
  period_to?: string;
  status?: CycleStatus | '';
}

/* ── Internal list response envelope ────────────────────────────────────── */

interface CycleListResponse {
  message: string;
  pagination: Pagination;
  results: CycleListItem[];
}

/* ── Query key factory ───────────────────────────────────────────────────── */

export const reconciliationKeys = {
  all: ['reconciliation'] as const,
  cycles: (params: ReconciliationCyclesParams) => ['reconciliation', 'cycles', params] as const,
  cycle: (cohortKey: string) => ['reconciliation', 'cycle', cohortKey] as const,
  emailPreview: (cohortKey: string, invoiceId: number) =>
    ['reconciliation', 'cycle', cohortKey, 'email-preview', invoiceId] as const,
  sendStatus: (cohortKey: string, batchId: string) =>
    ['reconciliation', 'cycle', cohortKey, 'send-status', batchId] as const,
};

/* ── Query helpers ──────────────────────────────────────────────────────── */

function buildCyclesQuery(params: ReconciliationCyclesParams): string {
  const clean: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '' && v !== null) {
      clean[k] = v as string | number;
    }
  }
  return qs.stringify(clean);
}

/* ── Hooks ──────────────────────────────────────────────────────────────── */

/**
 * `GET /reconciliation/cycles` — paginated list of reconciliation cycles.
 * Envelope: { message, pagination, results: CycleListItem[] }
 */
export function useReconciliationCycles(params: ReconciliationCyclesParams = {}) {
  return useQuery({
    queryKey: reconciliationKeys.cycles(params),
    queryFn: async (): Promise<CycleListResponse> => {
      const query = buildCyclesQuery({ page: 1, page_size: 20, ...params });
      const res = await dataCentralApi.get<CycleListResponse>(
        `/reconciliation/cycles${query ? `?${query}` : ''}`,
      );
      return res.data;
    },
  });
}

/**
 * `GET /reconciliation/cycles/{cohortKey}` — full cycle detail.
 * Envelope: { message, result: CycleDetail }
 */
export function useReconciliationCycle(cohortKey: string) {
  return useQuery({
    queryKey: reconciliationKeys.cycle(cohortKey),
    queryFn: async (): Promise<CycleDetail> => {
      const res = await dataCentralApi.get<{ message: string; result: CycleDetail }>(
        `/reconciliation/cycles/${encodeURIComponent(cohortKey)}`,
      );
      return res.data.result;
    },
    enabled: Boolean(cohortKey),
  });
}

/**
 * `GET /reconciliation/cycles/{cohortKey}/email-preview?invoice_id=`
 * Envelope: { message, result: { html, recipients } }
 * Not auto-fetched — call refetch() from the send-email dialog.
 */
export function useEmailPreview(cohortKey: string, invoiceId: number, opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: reconciliationKeys.emailPreview(cohortKey, invoiceId),
    queryFn: async (): Promise<EmailPreviewResult> => {
      const res = await dataCentralApi.get<{ message: string; result: EmailPreviewResult }>(
        `/reconciliation/cycles/${encodeURIComponent(cohortKey)}/email-preview?invoice_id=${invoiceId}`,
      );
      return res.data.result;
    },
    enabled: Boolean(cohortKey) && Boolean(invoiceId) && (opts?.enabled ?? false),
    staleTime: Infinity,
  });
}

/**
 * `POST /reconciliation/cycles/{cohortKey}/send-email`
 * Returns BatchStatus. 409 = batch already active; 400 = confirmation required.
 */
export function useSendReconciliationEmail(cohortKey: string) {
  return useMutation({
    mutationFn: async (body: SendEmailBody): Promise<BatchStatus> => {
      const res = await dataCentralApi.post<{ message: string; result: BatchStatus }>(
        `/reconciliation/cycles/${encodeURIComponent(cohortKey)}/send-email`,
        body,
      );
      return res.data.result;
    },
  });
}

/**
 * `GET /reconciliation/cycles/{cohortKey}/send-status?batch_id=`
 * Poll after a send to track progress.
 * Not auto-fetched — caller enables via `enabled` prop.
 */
export function useSendStatus(
  cohortKey: string,
  batchId: string,
  opts?: { enabled?: boolean; refetchInterval?: number },
) {
  return useQuery({
    queryKey: reconciliationKeys.sendStatus(cohortKey, batchId),
    queryFn: async (): Promise<BatchStatus> => {
      const res = await dataCentralApi.get<{ message: string; result: BatchStatus }>(
        `/reconciliation/cycles/${encodeURIComponent(cohortKey)}/send-status?batch_id=${encodeURIComponent(batchId)}`,
      );
      return res.data.result;
    },
    enabled: Boolean(cohortKey) && Boolean(batchId) && (opts?.enabled ?? false),
    refetchInterval: opts?.refetchInterval,
  });
}

/**
 * `POST /services/:serviceId/invoices/statement`
 * Runs a statement invoice for a service in the reconciliation context.
 * USER must trigger this — never called automatically.
 */
export function useRunStatement() {
  return useMutation({
    mutationFn: async (serviceId: number | string): Promise<{ message: string }> => {
      const res = await dataCentralApi.post<{ message: string }>(
        `/services/${serviceId}/invoices/statement`,
      );
      return res.data;
    },
  });
}

/**
 * `POST /services/:serviceId/invoices/statement/rollback`
 * Rolls back the latest statement for a service. Only valid when status === 'issued'
 * (i.e. has_active_snapshot). USER must trigger this — never called automatically.
 */
export function useRollbackStatement() {
  return useMutation({
    mutationFn: async (serviceId: number | string): Promise<{ message: string }> => {
      const res = await dataCentralApi.post<{ message: string }>(
        `/services/${serviceId}/invoices/statement/rollback`,
      );
      return res.data;
    },
  });
}

/* ── Pipeline run-log (statement pipeline dashboard) ──────────────────────── */

/** A pipeline run records one batch execution of a cohort through all stages. */
export type PipelineRunStatus = 'running' | 'done' | 'partial' | 'failed';
export type PipelineStageName = 'fetch' | 'generate' | 'merge' | 'sign' | 'send';

export interface PipelineRun {
  id: number;
  cohort_key: string;
  period: string | null;
  status: PipelineRunStatus;
  started_by: number | null;
  started_at: string | null;
  finished_at: string | null;
}

export interface PipelineStageError {
  ref_id?: number | string;
  ref_type?: string;
  message: string;
}

export interface PipelineStage {
  stage: PipelineStageName;
  status: 'pending' | 'running' | 'done' | 'partial' | 'failed';
  input_count: number;
  output_count: number;
  ok_count: number;
  skipped_count: number;
  failed_count: number;
  metrics?: Record<string, unknown> | null;
  errors?: PipelineStageError[] | null;
  started_at?: string | null;
  finished_at?: string | null;
}

export type PipelineRunDetail = PipelineRun & { stages: PipelineStage[] };

export const pipelineKeys = {
  runs: (cohortKey?: string) => ['reconciliation', 'pipeline', 'runs', cohortKey ?? 'all'] as const,
  run: (runId: number | string) => ['reconciliation', 'pipeline', 'run', runId] as const,
};

/**
 * `GET /reconciliation/pipeline/runs` — recent pipeline runs (most recent first).
 * Pass a cohortKey to filter to one cohort; omit to list across cohorts. Automation
 * writes the run cohort_key as a UUID, so the console lists runs rather than matching
 * the reconciliation window key.
 * Envelope: { message, pagination, results: PipelineRun[] }
 */
export function usePipelineRuns(cohortKey?: string) {
  return useQuery({
    queryKey: pipelineKeys.runs(cohortKey),
    queryFn: async (): Promise<PipelineRun[]> => {
      const q = cohortKey ? `cohort_key=${encodeURIComponent(cohortKey)}&` : '';
      const res = await dataCentralApi.get<{ results: PipelineRun[] }>(
        `/reconciliation/pipeline/runs?${q}page_size=50`,
      );
      return res.data.results ?? [];
    },
  });
}

/**
 * `GET /reconciliation/pipeline/runs/:id` — run + stages. Poll while running.
 */
export function usePipelineRun(
  runId: number | string | null,
  opts?: { enabled?: boolean; poll?: boolean },
) {
  return useQuery({
    queryKey: pipelineKeys.run(runId ?? 0),
    queryFn: async (): Promise<PipelineRunDetail> => {
      const res = await dataCentralApi.get<{ result: PipelineRunDetail }>(
        `/reconciliation/pipeline/runs/${runId}`,
      );
      return res.data.result;
    },
    enabled: Boolean(runId) && (opts?.enabled ?? true),
    // Poll only while the run is still executing; stop once it reaches a terminal state.
    refetchInterval: opts?.poll
      ? (query) => (query.state.data?.status === 'running' ? 3000 : false)
      : false,
  });
}

/**
 * `POST /automation/invoices/auto_merge` — merge a batch of invoices (retry stage).
 */
export function useAutoMergeInvoices() {
  return useMutation({
    mutationFn: async (invoiceIds: number[]): Promise<{ merged_count: number; merged_invoice_ids: number[] }> => {
      const res = await dataCentralApi.post<{ result: { merged_count: number; merged_invoice_ids: number[] } }>(
        `/automation/invoices/auto_merge`,
        { invoice_ids: invoiceIds },
      );
      return res.data.result;
    },
  });
}

/**
 * Generate statement invoices for a set of services (the Generate stage). Loops the
 * per-service endpoint with small concurrency and reports per-service failures.
 */
export function useGenerateStatements() {
  return useMutation({
    mutationFn: async (
      serviceIds: Array<number | string>,
    ): Promise<{ ok: number; failed: { id: number | string; error: string }[] }> => {
      const failed: { id: number | string; error: string }[] = [];
      let ok = 0;
      const CONCURRENCY = 4;
      for (let i = 0; i < serviceIds.length; i += CONCURRENCY) {
        const batch = serviceIds.slice(i, i + CONCURRENCY);
        await Promise.all(
          batch.map(async (id) => {
            try {
              await dataCentralApi.post(`/services/${id}/invoices/statement`);
              ok += 1;
            } catch (e) {
              failed.push({ id, error: e instanceof Error ? e.message : 'failed' });
            }
          }),
        );
      }
      return { ok, failed };
    },
  });
}

/**
 * `POST /automation/invoices/request-sign` — request MISA eSign for invoices (retry stage).
 * document_types defaults to both (reconciliation_statement + payment_request) on the backend.
 */
export function useRequestSign() {
  return useMutation({
    mutationFn: async (body: {
      invoice_ids: number[];
      document_types?: string[];
      image_source?: string;
    }): Promise<Record<string, unknown>> => {
      const res = await dataCentralApi.post<{ result: Record<string, unknown> }>(
        `/automation/invoices/request-sign`,
        body,
      );
      return res.data.result;
    },
  });
}

/* ── Plain API calls (for progressive pipeline execution — loop/poll friendly) ── */

/** Generate one service's statement invoice. */
export async function apiGenerateStatement(serviceId: number | string): Promise<void> {
  await dataCentralApi.post(`/services/${serviceId}/invoices/statement`);
}

/** Merge a batch of invoices (backend groups by customer_mailers). */
export async function apiAutoMerge(
  invoiceIds: number[],
): Promise<{ merged_count: number; merged_invoice_ids: number[] }> {
  const res = await dataCentralApi.post<{ result: { merged_count: number; merged_invoice_ids: number[] } }>(
    `/automation/invoices/auto_merge`,
    { invoice_ids: invoiceIds },
  );
  return res.data.result;
}

/** Request MISA eSign for a batch (≤15). Returns per-docType items with per-invoice status. */
export async function apiRequestSign(body: {
  invoice_ids: number[];
  document_types?: string[];
  image_source?: string;
}): Promise<Record<string, { submitted?: number; items?: { invoice_id: number; status: string; error?: string }[] }>> {
  const res = await dataCentralApi.post<{ result: Record<string, { submitted?: number; items?: { invoice_id: number; status: string; error?: string }[] }> }>(
    `/automation/invoices/request-sign`,
    body,
  );
  return res.data.result ?? {};
}

/** Start a reconciliation email batch; returns the batch status (incl. batch_id). */
export async function apiSendEmail(cohortKey: string, body: SendEmailBody): Promise<BatchStatus> {
  const res = await dataCentralApi.post<{ result: BatchStatus }>(
    `/reconciliation/cycles/${encodeURIComponent(cohortKey)}/send-email`,
    body,
  );
  return res.data.result;
}

/** Poll one email batch's progress. */
export async function apiSendStatus(cohortKey: string, batchId: string): Promise<BatchStatus> {
  const res = await dataCentralApi.get<{ result: BatchStatus }>(
    `/reconciliation/cycles/${encodeURIComponent(cohortKey)}/send-status?batch_id=${encodeURIComponent(batchId)}`,
  );
  return res.data.result;
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

/** Format a VND amount as a localized string. */
export function formatVND(amount: number): string {
  return amount.toLocaleString('vi-VN') + ' ₫';
}

/** Format an ISO date string (or YYYY-MM-DD) as DD/MM/YYYY for Vietnamese display. */
export function formatDateVN(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${d.getFullYear()}`;
}

/** "YYYY-MM-DD_YYYY-MM-DD" → "DD/MM/YYYY – DD/MM/YYYY" */
export function formatCohortKeyLabel(cohortKey: string): string {
  const parts = cohortKey.split('_');
  if (parts.length !== 2) return cohortKey;
  return `${formatDateVN(parts[0])} – ${formatDateVN(parts[1])}`;
}
